import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { PHONE, PHONE_HREF } from "@/lib/data";
import useSEO from "@/lib/useSEO";
import { ShieldCheck, CheckCircle2, ClipboardCheck, Wrench, Gauge, AlertTriangle, Phone } from "lucide-react";

const CHECKS = [
  { icon: Gauge, t: "Operator controls", d: "Steering, horn, warning lights, seat belt, deadman switch." },
  { icon: Wrench, t: "Hydraulics & mast", d: "Cylinders, hoses, chains, mast rollers, forks and carriage." },
  { icon: AlertTriangle, t: "Brakes & tyres", d: "Service brake, park brake, tyre condition and wear." },
  { icon: ClipboardCheck, t: "Load handling", d: "Data plate, attachments, rated capacity and stability." },
  { icon: ShieldCheck, t: "Safety systems", d: "Lights, alarms, mirrors, reversing beeper and load backrest." },
  { icon: CheckCircle2, t: "Compliance report", d: "Written inspection report aligned to AS 2359 safety requirements." },
];

const PACKAGES = [
  {
    id: "single",
    name: "Single Unit",
    tag: "1 forklift",
    price: "From $220",
    priceNote: "one-off / unit",
    description: "One-time safety inspection for a single forklift, on-site at your Brisbane workplace.",
    features: [
      "Full AS 2359 aligned inspection",
      "Written report with photos",
      "Fault priority ratings",
      "Follow-up repair quote",
    ],
  },
  {
    id: "fleet",
    name: "Fleet",
    tag: "2 – 10 forklifts",
    price: "From $180",
    priceNote: "per unit",
    description: "Discounted per-unit rate for businesses running multiple forklifts, inspected in a single visit.",
    features: [
      "Everything in Single Unit",
      "Consolidated fleet report",
      "Fleet compliance summary",
      "Priority booking",
    ],
    featured: true,
  },
  {
    id: "programme",
    name: "Monthly Programme",
    tag: "Ongoing compliance",
    price: "Custom",
    priceNote: "quoted",
    description: "Scheduled monthly or quarterly inspections to keep your fleet continuously compliant and safe.",
    features: [
      "Scheduled visits (monthly / quarterly)",
      "Compliance certificate per visit",
      "Preventative maintenance advice",
      "Dedicated account technician",
    ],
  },
];

export default function SafetyInspection() {
  useSEO({
    title: "Forklift Safety Inspection Brisbane — Forkfix",
    description: "Book a professional forklift safety inspection with Forkfix. AS 2359 aligned checks, written report, mobile service across Brisbane.",
    canonical: "https://forkfix.net/forklift-safety-inspection",
  });

  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", suburb: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const pickPackage = (pkg) => {
    setSelectedPackage(pkg.name);
    setForm(f => ({
      ...f,
      message: f.message?.includes("Package:") ? f.message : `Package: ${pkg.name}\n\nFleet size / details:\n`,
    }));
    setTimeout(() => {
      document.getElementById("request-inspection")?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { kind: "contact", ...form, extra: { reason: "Safety Inspection", package: selectedPackage || null } });
      setDone(true);
      toast.success("Request received — we'll be in touch.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div>
      <section className="bg-[#111111] text-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20 ff-diagonal-bg grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Compliance & Safety</div>
            <h1 className="font-display text-5xl md:text-7xl uppercase mt-3">Forklift Safety Inspection</h1>
            <p className="mt-5 text-white/75 max-w-xl">A thorough on-site inspection of your forklift by a qualified Forkfix technician. We check the mechanical, hydraulic and safety systems and issue a written report so your operators — and your business — stay compliant.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#request-inspection" className="ff-btn-primary" data-testid="hero-request-btn"><ClipboardCheck className="w-4 h-4" /> Request Inspection</a>
              <a href={PHONE_HREF} className="ff-btn-ghost text-white border-white hover:bg-white hover:text-[#111]" data-testid="hero-call-btn"><Phone className="w-4 h-4" /> {PHONE}</a>
            </div>
          </div>
          <div className="border border-white/15 bg-white/5 p-6 text-white">
            <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold">What you receive</div>
            <ul className="mt-4 space-y-3 text-sm text-white/90">
              {["Full on-site inspection at your workplace", "Written report with photos of any faults", "Recommended repairs and priority ratings", "AS 2359 aligned checklist", "Follow-up service quote if needed"].map(x => (
                <li key={x} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-[#D71920] shrink-0 mt-0.5" /> {x}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16 md:py-20">
        <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold text-center">Inspection scope</div>
        <h2 className="font-display text-4xl md:text-5xl uppercase text-center mt-2">What we check</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
          {CHECKS.map(({ icon: I, t, d }) => (
            <div key={t} className="ff-card p-6 hover:border-[#D71920] transition">
              <I className="w-8 h-8 text-[#D71920]" />
              <h3 className="font-display text-xl uppercase mt-3">{t}</h3>
              <p className="text-sm text-[#555] mt-2">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#f4f4f4] py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold text-center">Packages</div>
          <h2 className="font-display text-4xl md:text-5xl uppercase text-center mt-2">Choose the right plan</h2>
          <p className="text-center text-sm text-[#555] mt-3 max-w-2xl mx-auto">Whether you run a single forklift or a full fleet, we have an inspection plan to match. All packages include a written report and AS 2359 aligned checks.</p>

          <div className="grid md:grid-cols-3 gap-6 mt-12" data-testid="safety-packages">
            {PACKAGES.map((p) => {
              const active = selectedPackage === p.name;
              let btnCls;
              if (active) btnCls = "bg-[#111] text-white";
              else if (p.featured) btnCls = "bg-[#D71920] text-white hover:bg-[#111]";
              else btnCls = "border-2 border-[#111] text-[#111] hover:bg-[#111] hover:text-white";
              return (
                <div key={p.name} className={`relative bg-white border-2 p-8 transition ${p.featured ? "md:-mt-4 md:mb-4 border-[#D71920] shadow-[8px_8px_0_0_rgba(215,25,32,0.15)]" : "border-[#e5e5e5] hover:border-[#111]"}`} data-testid={`package-${p.id}`}>
                  {p.featured && <div className="absolute -top-3 left-8 bg-[#D71920] text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1">Most Popular</div>}
                  <div className="text-xs uppercase tracking-widest text-[#666] font-bold">{p.tag}</div>
                  <h3 className="font-display text-3xl uppercase mt-2">{p.name}</h3>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="font-display text-4xl text-[#D71920]">{p.price}</span>
                    <span className="text-xs text-[#666] uppercase tracking-widest">{p.priceNote}</span>
                  </div>
                  <p className="text-sm text-[#555] mt-4">{p.description}</p>
                  <ul className="mt-6 space-y-2 text-sm">
                    {p.features.map(f => (
                      <li key={f} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#D71920] shrink-0 mt-0.5" /> <span className="text-[#333]">{f}</span></li>
                    ))}
                  </ul>
                  <button type="button" onClick={() => pickPackage(p)}
                    className={`mt-8 w-full text-sm uppercase tracking-widest font-bold px-6 py-3 transition ${btnCls}`}
                    data-testid={`quote-${p.id}`}>
                    {active ? "Selected ✓" : "Get Quote"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#f4f4f4] pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold text-center">How it works</div>
          <h2 className="font-display text-4xl md:text-5xl uppercase text-center mt-2">Simple 3-step process</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              ["01", "Request", "Fill out the form or call us with your forklift details and preferred date."],
              ["02", "Inspect", "Our technician visits your site and completes the full safety inspection."],
              ["03", "Report", "You receive a written report with photos, findings and recommendations."],
            ].map(([n, t, d]) => (
              <div key={n} className="ff-card p-6 relative">
                <div className="font-display text-6xl text-[#D71920]/10 absolute top-3 right-4">{n}</div>
                <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold">Step {n}</div>
                <div className="font-display text-2xl uppercase mt-2">{t}</div>
                <p className="text-sm text-[#555] mt-2">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="request-inspection" className="max-w-3xl mx-auto px-6 py-16 md:py-20">
        <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold text-center">Request now</div>
        <h2 className="font-display text-4xl md:text-5xl uppercase text-center mt-2">Book an inspection</h2>
        {done ? (
          <div className="ff-card p-10 mt-10 text-center">
            <CheckCircle2 className="w-14 h-14 text-[#D71920] mx-auto" />
            <h3 className="font-display text-3xl uppercase mt-4">Request received</h3>
            <p className="text-[#555] mt-3">Thanks {form.name || "there"} — we'll be in touch shortly to confirm your safety inspection.</p>
            <Link to="/" className="ff-btn-dark mt-6">Back to Home</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="ff-card p-8 mt-10 grid md:grid-cols-2 gap-4" data-testid="safety-form">
            <Field label="Name" required><input value={form.name} onChange={e => set("name", e.target.value)} required className="ff-input" data-testid="safety-name" /></Field>
            <Field label="Company"><input value={form.company} onChange={e => set("company", e.target.value)} className="ff-input" data-testid="safety-company" /></Field>
            <Field label="Phone" required><input value={form.phone} onChange={e => set("phone", e.target.value)} required className="ff-input" data-testid="safety-phone" /></Field>
            <Field label="Email" required><input type="email" value={form.email} onChange={e => set("email", e.target.value)} required className="ff-input" data-testid="safety-email" /></Field>
            <Field label="Suburb" full><input value={form.suburb} onChange={e => set("suburb", e.target.value)} className="ff-input" data-testid="safety-suburb" /></Field>
            <Field label="Forklift details / message" full required>
              <textarea rows={4} value={form.message} onChange={e => set("message", e.target.value)} required placeholder="Make, model, quantity, preferred date" className="ff-input resize-none" data-testid="safety-message" />
            </Field>
            <div className="md:col-span-2">
              <button type="submit" disabled={sending} className="ff-btn-primary w-full md:w-auto" data-testid="safety-submit">
                {sending ? "Sending…" : "Request inspection"}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}

function Field({ label, required, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-xs uppercase tracking-widest text-[#666] font-bold">{label} {required && <span className="text-[#D71920]">*</span>}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
